import { memo, useCallback, useMemo } from 'react';

import { type SegmentedControlOption } from '@air/component-segmented-control';

import { CollectionNameSetting } from '~/components/SharePrivateCollectionModal/components/CollectionNameSetting';
import { EditableCollectionSharePrivateLink } from '~/components/SharePrivateCollectionModal/components/EditableCollectionSharePrivateLink';
import { ExpirationDateSetting } from '~/components/SharePrivateCollectionModal/components/ExpirationDateSetting';
import { ModalActionButtons } from '~/components/SharePrivateCollectionModal/components/ModalActionButtons';
import { ModalSection } from '~/components/SharePrivateCollectionModal/components/ModalSection';
import { PasscodeProtectionSetting } from '~/components/SharePrivateCollectionModal/components/PasscodeProtectionSetting';
import { PermissionSegmentField } from '~/components/SharePrivateCollectionModal/components/PermissionSegmentField';
import { PermissionToggleField } from '~/components/SharePrivateCollectionModal/components/PermissionToggleField';
import {
  PERMISSION_OPTION_ALLOW,
  PERMISSION_OPTION_HIDE,
  PERMISSION_OPTION_READ_ONLY,
} from '~/components/SharePrivateCollectionModal/consts';
import { useSharePrivateCollectionModal } from '~/components/SharePrivateCollectionModal/SharePrivateCollectionModalContext';
import { toPermissionMode } from '~/components/SharePrivateCollectionModal/utils';
import { useShareSettings } from '~/hooks/useShareSettings';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import {
  canChangeAssetCustomFields,
  canEditAssetDiscussions,
  canSeeAssetCustomFields,
  canViewAssetDiscussions,
} from '~/utils/permissions/assetPermissions';

export const SharePrivateCollectionModalForm = memo(() => {
  const {
    collectionProps,
    shortUrlProps,
    isSaving,
    isEditMode,
    updateLinkAccessPermission,
    updateAssetPermission,
    updateCommentPermissions,
    updateCustomFieldPermissions,
  } = useSharePrivateCollectionModal();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const { customFieldsEnabled, commentsEnabled } = useShareSettings();

  const canViewDiscussions = canViewAssetDiscussions(workspacePermissions);
  const canManageDiscussions = canEditAssetDiscussions(workspacePermissions);
  const canViewCustomFields = canSeeAssetCustomFields(workspacePermissions);
  const canEditCustomFields = canChangeAssetCustomFields(workspacePermissions);

  const commentsOptions = useMemo(() => {
    const options: SegmentedControlOption[] = [PERMISSION_OPTION_HIDE];
    if (canViewDiscussions && !(shortUrlProps.permissions.asset.canManageDiscussions && !canManageDiscussions)) {
      options.push(PERMISSION_OPTION_READ_ONLY);
    }
    if (canManageDiscussions) {
      options.push(PERMISSION_OPTION_ALLOW);
    }
    return options;
  }, [canViewDiscussions, canManageDiscussions, shortUrlProps.permissions.asset.canManageDiscussions]);
  const commentsPermissionValue = useMemo(() => {
    if (shortUrlProps.permissions.asset.canViewDiscussions && shortUrlProps.permissions.asset.canManageDiscussions) {
      return PERMISSION_OPTION_ALLOW.id;
    } else if (
      shortUrlProps.permissions.asset.canViewDiscussions &&
      !shortUrlProps.permissions.asset.canManageDiscussions
    ) {
      return PERMISSION_OPTION_READ_ONLY.id;
    } else {
      return PERMISSION_OPTION_HIDE.id;
    }
  }, [shortUrlProps.permissions.asset.canViewDiscussions, shortUrlProps.permissions.asset.canManageDiscussions]);

  const customFieldsOptions = useMemo(() => {
    const options: SegmentedControlOption[] = [PERMISSION_OPTION_HIDE];
    if (canViewCustomFields && !(shortUrlProps.permissions.customField.canManageApplications && !canEditCustomFields)) {
      options.push(PERMISSION_OPTION_READ_ONLY);
    }
    if (canEditCustomFields) {
      options.push(PERMISSION_OPTION_ALLOW);
    }
    return options;
  }, [canViewCustomFields, canEditCustomFields, shortUrlProps.permissions.customField.canManageApplications]);
  const customFieldsPermissionValue = useMemo(() => {
    if (shortUrlProps.permissions.customField.canView && shortUrlProps.permissions.customField.canManageApplications) {
      return PERMISSION_OPTION_ALLOW.id;
    } else if (
      shortUrlProps.permissions.customField.canView &&
      !shortUrlProps.permissions.customField.canManageApplications
    ) {
      return PERMISSION_OPTION_READ_ONLY.id;
    } else {
      return PERMISSION_OPTION_HIDE.id;
    }
  }, [shortUrlProps.permissions.customField.canView, shortUrlProps.permissions.customField.canManageApplications]);

  const handleLinkAccessChange = useCallback(
    (checked: boolean) => {
      updateLinkAccessPermission(checked);
    },
    [updateLinkAccessPermission],
  );

  const handleCanViewOriginalChange = useCallback(
    (checked: boolean) => {
      updateAssetPermission('canViewOriginal', checked);
    },
    [updateAssetPermission],
  );

  const handleCanViewAllVersionsChange = useCallback(
    (checked: boolean) => {
      updateAssetPermission('canViewAllVersions', checked);
    },
    [updateAssetPermission],
  );

  const handleCommentsPermissionChange = useCallback(
    (option: SegmentedControlOption) => {
      updateCommentPermissions(toPermissionMode(option));
    },
    [updateCommentPermissions],
  );

  const handleCustomFieldsPermissionChange = useCallback(
    (option: SegmentedControlOption) => {
      updateCustomFieldPermissions(toPermissionMode(option));
    },
    [updateCustomFieldPermissions],
  );

  const displayPermissionAndSecuritySection = (isEditMode && !shortUrlProps.disabled) || !isEditMode;

  return (
    <div className="flex flex-col gap-4 pt-4 pb-6">
      <div className="pb-2">
        <ModalSection label="Link name">
          <CollectionNameSetting />
        </ModalSection>
      </div>
      {isEditMode && (
        <ModalSection label="Link access">
          <PermissionToggleField
            label="Link enabled"
            id="disabled"
            checked={!shortUrlProps.disabled}
            onCheckedChange={handleLinkAccessChange}
            disabled={isSaving || !workspacePermissions?.asset.canViewOriginal}
          />
        </ModalSection>
      )}
      {displayPermissionAndSecuritySection && (
        <ModalSection label="Permissions">
          <PermissionToggleField
            label="Allow downloads"
            id="asset.canViewOriginal"
            checked={shortUrlProps.permissions.asset.canViewOriginal}
            onCheckedChange={handleCanViewOriginalChange}
            disabled={isSaving || !workspacePermissions?.asset.canViewOriginal}
          />
          <PermissionToggleField
            label="Show versions"
            id="asset.canViewAllVersions"
            checked={shortUrlProps.permissions.asset.canViewAllVersions}
            onCheckedChange={handleCanViewAllVersionsChange}
            disabled={isSaving}
          />
          <PermissionSegmentField
            label="Comments"
            value={commentsPermissionValue}
            onSelect={handleCommentsPermissionChange}
            options={commentsOptions}
            requiresFeature={!commentsEnabled}
          />
          <PermissionSegmentField
            label="Custom fields"
            value={customFieldsPermissionValue}
            onSelect={handleCustomFieldsPermissionChange}
            options={customFieldsOptions}
            requiresFeature={!customFieldsEnabled}
          />
        </ModalSection>
      )}
      {displayPermissionAndSecuritySection && (
        <ModalSection label="Security">
          <PasscodeProtectionSetting />
          <ExpirationDateSetting />
        </ModalSection>
      )}
      {!isEditMode && <ModalActionButtons />}
      {isEditMode && collectionProps.id && <EditableCollectionSharePrivateLink collectionId={collectionProps.id} />}
    </div>
  );
});

SharePrivateCollectionModalForm.displayName = 'SharePrivateCollectionModalForm';
